// src/components/QRPosterDesigner.tsx
import React, {
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from 'react';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import QRGenerator from './QRGenerator';
import styles from './QRPosterDesigner.module.scss';

/* ---------- Sizes ---------- */
type PosterSizeKey = 'A6' | 'A5' | 'A4' | 'Square' | 'Story';
const POSTER_SIZES: Record<
  PosterSizeKey,
  { w: number; h: number; pdf?: { wmm: number; hmm: number; orient: 'p' | 'l' } }
> = {
  A6:    { w: 1240, h: 1748, pdf: { wmm: 105, hmm: 148, orient: 'p' } },
  A5:    { w: 1748, h: 2480, pdf: { wmm: 148, hmm: 210, orient: 'p' } },
  A4:    { w: 2480, h: 3508, pdf: { wmm: 210, hmm: 297, orient: 'p' } },
  Square:{ w: 1080, h: 1080 },
  Story: { w: 1080, h: 1920 },
};

/* ---------- Zoom ---------- */
type ZoomMode = 'fit' | 'manual';

function useAutoFitScale(
  container: HTMLDivElement | null,
  content: HTMLDivElement | null,
  mode: ZoomMode,
  fw: number,
  fh: number,
  manualPercent: number
) {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    if (!container || !content) return;

    if (mode === 'manual') {
      setScale(Math.max(0.2, Math.min(1, manualPercent / 100)));
      return;
    }

    const apply = () => {
      const vw = Math.max(0, container.clientWidth);
      const vh = Math.max(0, container.clientHeight);
      const pw = content.offsetWidth || fw;
      const ph = content.offsetHeight || fh;
      // Fit must never overflow the viewport
      const s = Math.min(vw / pw, vh / ph, 1);
      setScale(Number.isFinite(s) ? Math.max(0.2, s) : 1);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(container);
    ro.observe(content);
    return () => ro.disconnect();
  }, [container, content, mode, fw, fh, manualPercent]);

  return scale;
}

/* ---------- Themes ---------- */
type PrimaryKey = 'amber' | 'red' | 'green' | 'blue' | 'violet' | 'ink' | 'custom';
type BgKey      = 'white' | 'gray' | 'amber50' | 'cyan50' | 'violet50' | 'orange50' | 'custom';

const PRIMARY_MAP: Record<Exclude<PrimaryKey,'custom'>, string> = {
  amber:  '#f59e0b', red: '#ef4444', green: '#10b981',
  blue:   '#3b82f6', violet: '#8b5cf6', ink: '#111827',
};
const BG_MAP: Record<Exclude<BgKey,'custom'>, string> = {
  white:'#ffffff', gray:'#f9fafb', amber50:'#fef3c7',
  cyan50:'#ecfeff', violet50:'#f3e8ff', orange50:'#fff7ed',
};

const DEFAULT_SOLID_COLORS: string[] = [
  '#000000','#4B5563','#6B7280','#D1D5DB','#FFFFFF',
  '#10B981','#22C55E','#A3E635','#67E8F9','#38BDF8',
  '#06B6D4','#3B82F6','#2563EB','#1D4ED8','#60A5FA',
  '#6366F1','#7C3AED','#8B5CF6','#A855F7','#F472B6',
  '#F43F5E','#EF4444','#F97316','#F59E0B','#B45309',
  '#16A34A','#059669','#0EA5E9','#8B5CF6','#EC4899',
];

/* ---------- Layers ---------- */
type LayerType = 'text' | 'qr' | 'logo' | 'image';
type Layer = {
  id: string;
  name?: string;
  type: LayerType;
  // geometry (fractions 0..1)
  xf: number; yf: number; wf: number; hf: number;
  rot?: number;
  z: number;
  locked?: boolean;
  visible?: boolean;

  // text props
  color?: string;
  textSize?: number;
  align?: 'left'|'center'|'right';

  // qr props
  qrFg?: string;
  qrBg?: string;

  // content
  text?: string;
  src?: string;
  value?: string;
};

function fracToPx(
  xf:number, yf:number, wf:number, hf:number, rot:number, z:number,
  W:number, H:number
) {
  return {
    x: Math.round(xf * W),
    y: Math.round(yf * H),
    w: Math.round(wf * W),
    h: Math.round(hf * H),
    rot, z
  };
}

function exportPixelRatio(meta: { wmm: number } | undefined, cssPxWidth: number) {
  if (!meta) return 2;
  const targetPx = (meta.wmm / 25.4) * 300; // 300dpi
  const pr = targetPx / cssPxWidth;
  return Math.max(1, Math.min(2.2, pr));
}

/* ---------- UI helpers ---------- */
function ColorDots({
  label, value, onChange, colors = DEFAULT_SOLID_COLORS, seeAllText = 'Voir tout',
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  colors?: string[];
  seeAllText?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className={styles.group}>
      <span className={styles.label}>{label}</span>
      <div className={styles.swatches} style={{ gap: 8, flexWrap: 'wrap' }}>
        {colors.map((c) => {
          const active = value.toLowerCase() === c.toLowerCase();
          return (
            <button
              key={c}
              type="button"
              className={styles.swatch}
              style={{
                backgroundColor: c,
                width: 28,
                height: 28,
                borderRadius: 9999,
                borderColor: active ? '#111827' : undefined,
                boxShadow: active ? '0 0 0 2px #111 inset' : undefined,
              }}
              aria-pressed={active}
              onClick={() => onChange(c)}
              title={c}
            />
          );
        })}
        <button type="button" className={styles.pill} onClick={() => inputRef.current?.click()}>
          {seeAllText}
        </button>
        <input
          ref={inputRef}
          type="color"
          value={value}
          onChange={(e)=>onChange(e.target.value)}
          style={{ position:'absolute', opacity:0, width:0, height:0, pointerEvents:'none' }}
          aria-hidden
          tabIndex={-1}
        />
      </div>
    </div>
  );
}

function Accordion({
  title, defaultOpen = false, children,
}: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  return (
    <details className={styles.accordion} open={defaultOpen}>
      <summary className={styles.accordionHeader}>
        <span>{title}</span>
        <svg className={styles.caret} viewBox="0 0 20 20" aria-hidden>
          <path d="M7 6l6 4-6 4V6z" fill="currentColor" />
        </svg>
      </summary>
      <div className={styles.accordionBody}>{children}</div>
    </details>
  );
}

/* ---------- Component ---------- */
interface Props {
  defaultBusinessName?: string;
  defaultQRValue?: string;
}

const QRPosterDesigner: React.FC<Props> = ({
  defaultBusinessName = '',
  defaultQRValue = '',
}) => {
  const [businessName, setBusinessName] = useState(defaultBusinessName);
  const [qrValue, setQrValue]           = useState(defaultQRValue);
  const [title, setTitle]               = useState('Laissez-nous un avis');
  const [cta, setCta]                   = useState('Scannez le QR pour laisser votre avis');

  const [primary, setPrimary]           = useState<string>(PRIMARY_MAP.amber);
  const [bg, setBg]                     = useState<string>(BG_MAP.white);

  const [sizeKey, setSizeKey]           = useState<PosterSizeKey>('A5');

  // NEW: zoom slider (20–100) + mode
  const [zoomMode, setZoomMode]         = useState<ZoomMode>('fit');
  const [manualZoom, setManualZoom]     = useState<number>(60);

  const [bgImageUrl, setBgImageUrl]     = useState<string | null>(null);
  const [showUrl, setShowUrl]           = useState(true);

  const rootRef   = useRef<HTMLDivElement>(null);
  const stageRef  = useRef<HTMLDivElement>(null);
  const posterRef = useRef<HTMLDivElement>(null);

  const size  = POSTER_SIZES[sizeKey];
  const scale = useAutoFitScale(stageRef.current, posterRef.current, zoomMode, size.w, size.h, manualZoom);

  // Keep in sync when props change later
  useEffect(() => setBusinessName(defaultBusinessName), [defaultBusinessName]);
  useEffect(() => setQrValue(defaultQRValue), [defaultQRValue]);

  const primaryKey: PrimaryKey =
    (Object.entries(PRIMARY_MAP).find(([,hex]) => hex === primary)?.[0] as PrimaryKey) || 'custom';
  const bgKey: BgKey =
    (Object.entries(BG_MAP).find(([,hex]) => hex === bg)?.[0] as BgKey) || 'custom';

  // Push CSS vars
  useEffect(() => {
    const el = rootRef.current; if (!el) return;
    if (primaryKey === 'custom') el.style.setProperty('--primary', primary);
    else el.style.removeProperty('--primary');
    if (bgKey === 'custom') el.style.setProperty('--bg', bg);
    else el.style.removeProperty('--bg');
    const z = zoomMode === 'fit' ? scale : Math.max(0.2, Math.min(1, manualZoom / 100));
    el.style.setProperty('--scale', String(z));
  }, [primary, bg, primaryKey, bgKey, zoomMode, scale, manualZoom]);

  // Poster background image
  useEffect(() => {
    if (!posterRef.current) return;
    if (bgImageUrl) posterRef.current.style.setProperty('--bg-image', `url("${bgImageUrl}")`);
    else posterRef.current.style.removeProperty('--bg-image');
  }, [bgImageUrl]);

  const shortUrl = useMemo(() => {
    try {
      if (!qrValue) return '';
      const u = new URL(qrValue, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      return (u.hostname + u.pathname).replace(/\/$/, '');
    } catch { return qrValue.slice(0, 60); }
  }, [qrValue]);

  // Initial layers
  const [layers, setLayers] = useState<Layer[]>(() => [
    { id:'title',    name:'Texte: Titre',      type:'text', text: 'Laissez-nous un avis', xf:.06, yf:.06, wf:.80,  hf:.10, z:12, color:'#111827', align:'left' },
    { id:'subtitle', name:'Texte: Sous-titre', type:'text', text: defaultBusinessName || 'Votre établissement', xf:.06, yf:.13, wf:.80,  hf:.08, z:12, color:'#111827', align:'left' },
    { id:'qr',       name:'QR code',           type:'qr',   value: defaultQRValue || '', xf:.275, yf:.35, wf:.45, hf:.45, z:10, qrFg:'#000000', qrBg:'#ffffff' },
    { id:'cta',      name:'Texte: CTA',        type:'text', text: 'Scannez le QR pour laisser votre avis', xf:.06, yf:.85, wf:.70, hf:.08, z:12, color:'#111827', align:'left' },
    { id:'url',      name:'URL courte',        type:'text', text: '', xf:.78, yf:.86, wf:.20, hf:.06, z:12, color:'#111827', align:'right', visible: true },
    { id:'logo',     name:'Logo',              type:'logo', src:'', xf:.06, yf:.22, wf:.16, hf:.16, z:11 },
  ]);
  const [selectedId, setSelectedId] = useState<string | null>('title');

  const selectedLayer = layers.find(l => l.id === selectedId) || null;
  const updateSelected = (partial: Partial<Layer>) => {
    if (!selectedId) return;
    setLayers(ls => ls.map(l => l.id === selectedId ? { ...l, ...partial } : l));
  };
  const bringToFront = (id: string) => setLayers(ls => {
    const top = (ls.reduce((m,l)=>Math.max(m,l.z),0) || 0) + 1;
    return ls.map(l => l.id === id ? { ...l, z: top } : l);
  });

  // Mirror sidebar edits
  useEffect(() => { setLayers(ls => ls.map(l => l.id==='title'    ? {...l, text: title} : l)); }, [title]);
  useEffect(() => { setLayers(ls => ls.map(l => l.id==='subtitle' ? {...l, text: (businessName || 'Votre établissement')} : l)); }, [businessName]);
  useEffect(() => { setLayers(ls => ls.map(l => l.id==='cta'      ? {...l, text: cta} : l)); }, [cta]);
  useEffect(() => { setLayers(ls => ls.map(l => l.id==='qr'       ? {...l, value: qrValue} : l)); }, [qrValue]);
  useEffect(() => {
    setLayers(ls => ls.map(l => l.id==='url' ? {...l, text: shortUrl, visible: showUrl} : l));
  }, [showUrl, shortUrl]);

  /* ---------- Keyboard: arrows + delete ---------- */
  useEffect(() => {
    const host = rootRef.current; if (!host) return;

    const isTyping = () => {
      const ae = document.activeElement as HTMLElement | null;
      return !!ae && (
        ae.tagName === 'INPUT' ||
        ae.tagName === 'TEXTAREA' ||
        ae.isContentEditable
      );
    };

    const onKey = (e: KeyboardEvent) => {
      if (!selectedId) return;

      // Delete layer
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isTyping()) {
        e.preventDefault();
        setLayers(ls => ls.filter(l => l.id !== selectedId));
        setSelectedId(null);
        return;
      }

      // Nudge with arrows
      const step = e.shiftKey ? 10 : 1;
      if (['ArrowUp','ArrowDown','ArrowLeft','ArrowRight'].includes(e.key)) e.preventDefault();
      setLayers(ls => ls.map(l => {
        if (l.id !== selectedId || l.locked) return l;
        let {xf,yf,wf,hf} = l;
        if (e.key==='ArrowLeft')  xf = Math.max(0, xf - step/size.w);
        if (e.key==='ArrowRight') xf = Math.min(1 - wf, xf + step/size.w);
        if (e.key==='ArrowUp')    yf = Math.max(0, yf - step/size.h);
        if (e.key==='ArrowDown')  yf = Math.min(1 - hf, yf + step/size.h);
        return {...l, xf, yf};
      }));
    };

    host.addEventListener('keydown', onKey);
    return () => host.removeEventListener('keydown', onKey);
  }, [selectedId, size.w, size.h]);

  /* ---------- Files ---------- */
  function readAsDataURL(file: File): Promise<string> {
    return new Promise((resolve,reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  }
  async function onUploadLogo(file: File | null) {
    if (!file) { setLayers(ls => ls.map(l => l.id==='logo' ? {...l, src: ''} : l)); return; }
    const src = await readAsDataURL(file);
    setLayers(ls => {
      const has = ls.some(l => l.id==='logo');
      return has ? ls.map(l => l.id==='logo' ? {...l, src, visible:true} : l)
                 : ls.concat([{ id:'logo', name:'Logo', type:'logo', src, xf:.06, yf:.22, wf:.16, hf:.16, z: (ls.reduce((m,x)=>Math.max(m,x.z),0)||0)+1 }]);
    });
  }
  async function onUploadBg(file: File | null) {
    if (!file) { setBgImageUrl(null); return; }
    setBgImageUrl(await readAsDataURL(file));
  }
  async function onAddImage(file: File | null) {
    if (!file) return;
    const src = await readAsDataURL(file);
    setLayers(ls => ls.concat([{
      id: `img_${Date.now()}`, name: 'Image', type:'image', src, xf:.55, yf:.15, wf:.32, hf:.32, z: (ls.reduce((m,x)=>Math.max(m,x.z),0)||0)+1
    }]));
  }
  function addText() {
    setLayers(ls => ls.concat([{
      id:`txt_${Date.now()}`, name:'Texte', type:'text', text:'Double-cliquez pour éditer',
      xf:.10, yf:.10, wf:.5, hf:.10, z:(ls.reduce((m,x)=>Math.max(m,x.z),0)||0)+1, color:'#111827', align:'left'
    }]));
  }

  /* ---------- Export ---------- */
  async function downloadPNG() {
    if (!posterRef.current) return;
    const pr = exportPixelRatio(size.pdf, size.w);
    const dataUrl = await toPng(posterRef.current, { pixelRatio: pr, cacheBust: true });
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${(businessName || 'poster').replace(/\s+/g, '-').toLowerCase()}-${sizeKey}.png`;
    a.click();
  }
  async function downloadPDF() {
    if (!posterRef.current) return;
    const pr = exportPixelRatio(size.pdf, size.w);
    const dataUrl = await toPng(posterRef.current, { pixelRatio: pr, cacheBust: true });
    const meta = size.pdf || { wmm: 210, hmm: 297, orient: 'p' as const };
    const pdf  = new jsPDF(meta.orient, 'mm', [meta.wmm, meta.hmm]);
    pdf.addImage(dataUrl, 'PNG', 0, 0, meta.wmm, meta.hmm);
    pdf.save(`${(businessName || 'poster').replace(/\s+/g, '-').toLowerCase()}-${sizeKey}.pdf`);
  }

  return (
    <div
      className={styles.root}
      ref={rootRef}
      data-size={sizeKey}
      data-theme={primaryKey}
      data-bg={bgKey}
      tabIndex={0}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <div className={styles.dot} />
          <h1>Affiche QR</h1>
        </div>
        <div className={styles.actions}>
          <button className={styles.btn} onClick={downloadPNG} aria-label="Télécharger en PNG">⬇️ PNG</button>
          <button className={styles.btnPrimary} onClick={downloadPDF} aria-label="Télécharger en PDF">⬇️ PDF</button>
        </div>
      </div>

      <div className={styles.main}>
        {/* LEFT: accordion sidebar */}
        <div className={styles.leftCol}>
          <Accordion title="Taille & Zoom" defaultOpen>
            <div className={styles.group}>
              <span className={styles.label}>Taille</span>
              <div className={styles.pills}>
                {(['A6','A5','A4','Square','Story'] as PosterSizeKey[]).map(k => (
                  <button
                    key={k}
                    className={`${styles.pill} ${k === sizeKey ? styles.pillActive : ''}`}
                    onClick={() => setSizeKey(k)}
                    aria-pressed={k === sizeKey}
                  >{k}</button>
                ))}
              </div>
            </div>

            <div className={styles.group}>
              <span className={styles.label}>Zoom</span>

              <div className={styles.segment} style={{ alignItems: 'center' }}>
                <button
                  className={`${styles.segmentBtn} ${zoomMode==='fit' ? styles.segmentBtnActive : ''}`}
                  onClick={() => setZoomMode('fit')}
                  aria-pressed={zoomMode==='fit'}
                >
                  Adapter à l’écran
                </button>

                <button
                  className={`${styles.segmentBtn} ${zoomMode==='manual' ? styles.segmentBtnActive : ''}`}
                  onClick={() => setZoomMode('manual')}
                  aria-pressed={zoomMode==='manual'}
                >
                  Manuel
                </button>

                {zoomMode === 'manual' && (
                  <div className={styles.rangeRow} style={{ marginLeft: 8 }}>
                    <span style={{ width: 56, textAlign: 'right' }}>{manualZoom}%</span>
                    <input
                      type="range"
                      min={20}
                      max={100}
                      step={1}
                      value={manualZoom}
                      onChange={(e)=>setManualZoom(Number(e.target.value))}
                      aria-label="Zoom manuel"
                    />
                  </div>
                )}
              </div>
            </div>
          </Accordion>

          <Accordion title="Couleurs">
            <ColorDots label="Couleur (accent)" value={primary} onChange={setPrimary} />
            <ColorDots label="Fond (arrière-plan)" value={bg} onChange={setBg} />
          </Accordion>

          <Accordion title="Contenu">
            <div className={styles.inputsCard}>
              <div className={styles.inputItem}>
                <label>Nom de l’établissement</label>
                <input value={businessName} onChange={e=>setBusinessName(e.target.value)} placeholder="Ex: Café du Centre" />
              </div>
              <div className={styles.inputItem}>
                <label>Texte principal</label>
                <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Laissez-nous un avis" />
              </div>
              <div className={styles.inputItem}>
                <label>CTA</label>
                <input value={cta} onChange={e=>setCta(e.target.value)} placeholder="Scannez le QR pour laisser votre avis" />
              </div>
              <div className={styles.inputItemFull}>
                <label>URL / valeur du QR</label>
                <input value={qrValue} onChange={e=>setQrValue(e.target.value)} placeholder="https://... ou /leave-review/:businessId" />
                <small>Astuce : utilisez vos liens “Formulaire d’avis public”.</small>
              </div>
              <div className={styles.group}>
                <span className={styles.label}>URL courte</span>
                <label className={styles.switch} aria-label="Afficher l’URL courte">
                  <input type="checkbox" checked={showUrl} onChange={e=>setShowUrl(e.target.checked)} />
                  <span />
                </label>
              </div>
            </div>
          </Accordion>

          <Accordion title="Médias (logo / fond / image)">
            <div className={styles.groupGrow}>
              <label className={styles.fileBtn}>
                <input type="file" accept="image/*" onChange={(e) => onUploadLogo(e.target.files?.[0] || null)} />
                Logo (couche)
              </label>
              <label className={styles.fileBtn}>
                <input type="file" accept="image/*" onChange={(e) => onUploadBg(e.target.files?.[0] || null)} />
                Image de fond
              </label>
              <label className={styles.fileBtn}>
                <input type="file" accept="image/*" onChange={(e) => onAddImage(e.target.files?.[0] || null)} />
                Ajouter image
              </label>
              <button className={styles.fileBtn} onClick={addText}>+ Texte</button>
            </div>
          </Accordion>

          {selectedLayer && (
            <Accordion title="Propriétés du texte">
              {selectedLayer.type === 'text' && (() => {
                const fallbackSize = Math.round((selectedLayer.hf || 0.1) * size.h * 0.35);
                const sliderValue = selectedLayer.textSize ?? fallbackSize;
                return (
                  <div className={styles.group}>
                    <span className={styles.label}>Texte</span>
                    <label className={styles.colorInput} title="Couleur du texte">
                      <input
                        type="color"
                        value={selectedLayer.color || '#111827'}
                        onChange={(e)=>updateSelected({ color: e.target.value })}
                      />
                    </label>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className={styles.pill} onClick={()=>updateSelected({ align:'left' })}>↤</button>
                      <button className={styles.pill} onClick={()=>updateSelected({ align:'center' })}>↔</button>
                      <button className={styles.pill} onClick={()=>updateSelected({ align:'right' })}>↦</button>
                    </div>
                    <div className={styles.rangeRow}>
                      <span>Taille</span>
                      <input
                        type="range" min={10} max={200} step={1}
                        value={sliderValue}
                        onChange={(e)=>updateSelected({ textSize: Number(e.target.value) || undefined })}
                      />
                    </div>
                  </div>
                );
              })()}
              {selectedLayer.type === 'qr' && (
                <div className={styles.group}>
                  <span className={styles.label}>QR</span>
                  <div style={{ display:'flex', gap:12 }}>
                    <label className={styles.colorInput}><input type="color" value={selectedLayer.qrFg || '#000000'} onChange={(e)=>updateSelected({ qrFg: e.target.value })} /></label>
                    <label className={styles.colorInput}><input type="color" value={selectedLayer.qrBg || '#ffffff'} onChange={(e)=>updateSelected({ qrBg: e.target.value })} /></label>
                  </div>
                  <small>Le QR reste centré dans son cadre.</small>
                </div>
              )}
            </Accordion>
          )}

          <Accordion title="Calques">
            <div className={styles.layersCard} style={{ boxShadow: 'none', background: 'transparent', padding: 0 }}>
              <div className={styles.layersHeader}>Calques</div>
              <ul className={styles.layerList}>
                {layers.slice().sort((a,b)=>b.z-a.z).map(l => (
                  <li
                    key={l.id}
                    className={`${styles.layerRow} ${selectedId===l.id?styles.layerRowActive:''}`}
                    onClick={()=>setSelectedId(l.id)}
                  >
                    <span className={styles.layerName}>{l.name || l.id}</span>
                    <div className={styles.layerActions}>
                      <button
                        title="Afficher/Masquer"
                        onClick={(e)=>{ e.stopPropagation(); setLayers(ls=>ls.map(x=>x.id===l.id?{...x,visible:!(x.visible!==false)}:x)); }}
                      >
                        {l.visible===false ? '🙈' : '👁️'}
                      </button>
                      <button
                        title="Bloquer/Débloquer"
                        onClick={(e)=>{ e.stopPropagation(); setLayers(ls=>ls.map(x=>x.id===l.id?{...x,locked:!x.locked}:x)); }}
                      >
                        {l.locked ? '🔒' : '🔓'}
                      </button>
                      <button
                        title="Amener au premier plan"
                        onClick={(e)=>{ e.stopPropagation(); bringToFront(l.id); }}
                      >
                        ⤴︎
                      </button>
                      {/* NEW: Delete from list */}
                      <button
                        title="Supprimer"
                        onClick={(e)=>{ e.stopPropagation(); setLayers(ls=>ls.filter(x=>x.id!==l.id)); if (selectedId===l.id) setSelectedId(null); }}
                      >
                        🗑️
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Accordion>
        </div>

        {/* RIGHT: stage */}
        <div className={styles.viewport}>
          <div ref={stageRef} className={styles.stage}>
            <div className={styles.scaledBox}>
              <div className={styles.scaleInner}>
                <div
                  ref={posterRef}
                  className={styles.poster}
                  onMouseDown={()=>setSelectedId(null)}
                >
                  {layers
                    .filter(l => l.visible !== false)
                    .sort((a,b)=>a.z-b.z)
                    .map(layer => (
                      <LayerView
                        key={layer.id}
                        layer={layer}
                        W={size.w}
                        H={size.h}
                        selected={selectedId === layer.id}
                        onSelect={() => { setSelectedId(layer.id); bringToFront(layer.id); }}
                        onChange={(partial) =>
                          setLayers(prev => prev.map(x => x.id===layer.id ? {...x, ...partial} : x))
                        }
                        getPosterRect={() => posterRef.current!.getBoundingClientRect()}
                      />
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRPosterDesigner;

/* ---------- LayerView ---------- */
function LayerView({
  layer, W, H, selected, onSelect,
  onChange, getPosterRect
}: {
  layer: Layer; W: number; H: number; selected: boolean;
  onSelect: () => void;
  onChange: (partial: Partial<Layer>) => void;
  getPosterRect: () => DOMRect;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);

  // Single active pointer
  const activePointerId = useRef<number | null>(null);

  type Mode = 'drag' | 'resize' | 'rotate' | null;
  type Handle = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';
  const state = useRef<{
    mode: Mode;
    handle?: Handle;
    sx:number; sy:number;
    start:{ x:number;y:number;w:number;h:number;rot:number };
  } | null>(null);

  const setVars = (x:number,y:number,w:number,h:number,rot:number,z:number) => {
    const el = ref.current; if (!el) return;
    el.style.setProperty('--x', x + 'px');
    el.style.setProperty('--y', y + 'px');
    el.style.setProperty('--w', w + 'px');
    el.style.setProperty('--h', h + 'px');
    el.style.setProperty('--rot', (rot ?? 0) + 'deg');
    (el.style as any).zIndex = String(z);
  };

  const { xf, yf, wf, hf, rot = 0, z } = layer;

  useLayoutEffect(() => {
    const g = fracToPx(xf, yf, wf, hf, rot ?? 0, z, W, H);
    setVars(g.x, g.y, g.w, g.h, g.rot, g.z);
  }, [xf, yf, wf, hf, rot, z, W, H]);

  // push text vars to CSS
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    if (layer.type === 'text') {
      if (layer.color) el.style.setProperty('--text-color', layer.color);
      else el.style.removeProperty('--text-color');
      if (layer.textSize) el.style.setProperty('--text-size', `${layer.textSize}px`);
      else el.style.removeProperty('--text-size');
    }
  }, [layer.type, layer.color, layer.textSize]);

  // global end-of-interaction
  useEffect(() => {
    const end = () => {
      const el = ref.current;
      if (el && activePointerId.current !== null) {
        try { el.releasePointerCapture(activePointerId.current); } catch {}
      }
      activePointerId.current = null;
      state.current = null;
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    window.addEventListener('blur', end);
    return () => {
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      window.removeEventListener('blur', end);
    };
  }, []);

  const captureHere = (e: React.PointerEvent) => {
    try { (e.currentTarget as Element).setPointerCapture(e.pointerId); } catch {}
    const el = ref.current; if (el) { try { el.setPointerCapture(e.pointerId); } catch {} }
    activePointerId.current = e.pointerId;
  };

  // enter edit mode (text)
  const enterEdit = () => {
    if (layer.locked || layer.type !== 'text') return;
    setEditing(true);
    requestAnimationFrame(() => {
      const el = textRef.current;
      if (!el) return;
      el.focus();
      const sel = window.getSelection?.();
      const range = document.createRange?.();
      if (sel && range) {
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    });
  };

  const startDrag = (e: React.PointerEvent) => {
    if (layer.locked || editing) return;
    if ((e as any).detail >= 2 && layer.type === 'text') return; // let dbl-click edit
    if ((e.target as HTMLElement).closest(`.${styles.handle}`)) return;
    e.preventDefault();
    const g = fracToPx(xf, yf, wf, hf, rot ?? 0, z, W, H);
    state.current = { mode:'drag', sx:e.clientX, sy:e.clientY, start: {x:g.x,y:g.y,w:g.w,h:g.h,rot:g.rot} };
    captureHere(e);
    onSelect();
  };

  const startResize = (handle: Handle) => (e: React.PointerEvent) => {
    if (layer.locked) return;
    e.preventDefault();
    const g = fracToPx(xf, yf, wf, hf, rot ?? 0, z, W, H);
    state.current = { mode:'resize', handle, sx:e.clientX, sy:e.clientY, start: {x:g.x,y:g.y,w:g.w,h:g.h,rot:g.rot} };
    captureHere(e);
    onSelect();
  };

  const startRotate = (e: React.PointerEvent) => {
    if (layer.locked) return;
    e.preventDefault();
    const g = fracToPx(xf, yf, wf, hf, rot ?? 0, z, W, H);
    state.current = { mode:'rotate', sx:e.clientX, sy:e.clientY, start: {x:g.x,y:g.y,w:g.w,h:g.h,rot:g.rot} };
    captureHere(e);
    onSelect();
  };

  const onMove = (e: React.PointerEvent) => {
    if (!state.current) return;
    if (activePointerId.current !== null && e.pointerId !== activePointerId.current) return;
    e.preventDefault();

    const rect = getPosterRect();
    const kx = W / rect.width;
    const ky = H / rect.height;
    const dx = (e.clientX - state.current.sx) * kx;
    const dy = (e.clientY - state.current.sy) * ky;

    const s = state.current.start;
    const clamp = (x:number, min:number, max:number) => Math.max(min, Math.min(max, x));
    const snap = (v:number, targets:number[], tol=8) => {
      for (const t of targets) if (Math.abs(v - t) < tol) return t;
      return v;
    };

    if (state.current.mode === 'drag') {
      let nx = clamp(s.x + dx, 0, W - s.w);
      let ny = clamp(s.y + dy, 0, H - s.h);
      nx = snap(nx, [0, (W - s.w)/2, W - s.w]);
      ny = snap(ny, [0, (H - s.h)/2, H - s.h]);
      setVars(nx, ny, s.w, s.h, rot ?? 0, z);
      onChange({ xf: nx / W, yf: ny / H });
      return;
    }

    if (state.current.mode === 'resize' && state.current.handle) {
      let { x, y, w, h } = s;
      let nx = x, ny = y, nw = w, nh = h;

      const handle = state.current.handle;
      if (handle.includes('e')) nw = clamp(w + dx, 40, W - x);
      if (handle.includes('s')) nh = clamp(h + dy, 40, H - y);
      if (handle.includes('w')) { nx = clamp(x + dx, 0, x + w - 40); nw = clamp(w - dx, 40, W - nx); }
      if (handle.includes('n')) { ny = clamp(y + dy, 0, y + h - 40); nh = clamp(h - dy, 40, H - ny); }

      // QR stays square
      if (layer.type === 'qr') {
        const side = Math.min(nw, nh);
        nw = side; nh = side;
        if (handle.includes('w')) nx = x + (w - side);
        if (handle.includes('n')) ny = y + (h - side);
      }

      // Snap to edges/center/halves
      nw = snap(nw, [W, W/2]);
      nh = snap(nh, [H, H/2]);
      nx = snap(nx, [0, (W - nw)/2, W - nw]);
      ny = snap(ny, [0, (H - nh)/2, H - nh]);

      setVars(nx, ny, nw, nh, rot ?? 0, z);
      onChange({ xf: nx / W, yf: ny / H, wf: nw / W, hf: nh / H });
      return;
    }

    if (state.current.mode === 'rotate') {
      const cx = s.x + s.w/2;
      const cy = s.y + s.h/2;
      const px = s.x + (e.clientX - state.current.sx) * kx + s.w/2;
      const py = s.y + (e.clientY - state.current.sy) * ky + s.h/2;
      const ang = Math.atan2(py - cy, px - cx);
      const deg = (ang * 180) / Math.PI;
      const snapped = snap(deg, [0, 45, 90, 135, 180, -45, -90, -135], 4);
      setVars(s.x, s.y, s.w, s.h, snapped, z);
      onChange({ rot: snapped });
    }
  };

  const endAny = () => {
    const el = ref.current;
    if (el && activePointerId.current !== null) {
      try { el.releasePointerCapture(activePointerId.current); } catch {}
    }
    activePointerId.current = null;
    state.current = null;
  };

  const onTextKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      setEditing(false);
      (e.currentTarget as HTMLDivElement).blur();
    }
  };

  // QR inner sizing
  const qrBoxRef = useRef<HTMLDivElement>(null);
  const [qrInnerSide, setQrInnerSide] = useState<number>(0);
  useLayoutEffect(() => {
    if (layer.type !== 'qr') return;
    const el = qrBoxRef.current;
    const g = fracToPx(xf, yf, wf, hf, rot ?? 0, z, W, H);
    const side = Math.min(g.w, g.h);
    let pad = 24, brd = 0;
    if (el) {
      const cs = getComputedStyle(el);
      pad = (parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight)) || 24;
      brd = (parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth)) || 0;
    }
    const safe = 4;
    setQrInnerSide(Math.max(0, Math.floor(side - pad - brd - safe)));
  }, [layer.type, xf, yf, wf, hf, rot, z, W, H]);

  const content = (() => {
    if (layer.type === 'text') {
      return (
        <div
          ref={textRef}
          className={styles.text}
          contentEditable={editing}
          suppressContentEditableWarning
          tabIndex={-1}
          onDoubleClick={(e)=>{ e.stopPropagation(); }}
          onPointerDown={(e)=> { if (editing) e.stopPropagation(); }}
          onBlur={(e)=> { onChange({ text: (e.target as HTMLDivElement).innerText }); setEditing(false); }}
          onKeyDown={onTextKeyDown}
        >
          {layer.text}
        </div>
      );
    }
    if (layer.type === 'qr') {
      return (
        <div ref={qrBoxRef} className={styles.qrBox}>
          <QRGenerator
            value={layer.value || ''}
            size={Math.max(0, qrInnerSide)}
            fgColor={layer.qrFg || '#000000'}
            bgColor={layer.qrBg || '#ffffff'}
          />
        </div>
      );
    }
    if ((layer.type === 'logo' || layer.type === 'image') && layer.src) {
      return <img className={styles.media} src={layer.src} alt="" draggable={false} />;
    }
    return null;
  })();

  return (
    <div
      ref={ref}
      className={`${styles.layer} ${selected ? styles.layerSelected : ''}`}
      data-type={layer.type}
      data-editing={editing ? 'true' : 'false'}
      data-align={layer.align || 'left'}
      onPointerDown={startDrag}
      onDoubleClick={(e)=>{ if (layer.type==='text') { e.stopPropagation(); enterEdit(); } }}
      onPointerMove={onMove}
      onPointerUp={endAny}
      onPointerCancel={endAny}
      role="group"
      aria-label={`${layer.type} layer`}
      onClick={onSelect}
    >
      {content}
      {!layer.locked && selected && !editing && (
        <>
          <div className={`${styles.handle} ${styles.h_n}`}  onPointerDown={startResize('n')} />
          <div className={`${styles.handle} ${styles.h_ne}`} onPointerDown={startResize('ne')} />
          <div className={`${styles.handle} ${styles.h_e}`}  onPointerDown={startResize('e')} />
          <div className={`${styles.handle} ${styles.h_se}`} onPointerDown={startResize('se')} />
          <div className={`${styles.handle} ${styles.h_s}`}  onPointerDown={startResize('s')} />
          <div className={`${styles.handle} ${styles.h_sw}`} onPointerDown={startResize('sw')} />
          <div className={`${styles.handle} ${styles.h_w}`}  onPointerDown={startResize('w')} />
          <div className={`${styles.handle} ${styles.h_nw}`} onPointerDown={startResize('nw')} />
          <div className={styles.rotHandle} onPointerDown={startRotate} title="Rotation" />
        </>
      )}
    </div>
  );
}
